import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemText,
  CircularProgress,
  Button,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RecentEmailModal, RecentEmailAttachementDownload } from "api-services";
import "../../users/components/css/recent_email.css";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import moment from "moment";
import { ShowSuccessMessage, ShowErrorMessage } from "helpers";
import { useMaterialUIController } from "context";

const RecentEmails = ({
  open,
  handleClose,
  darkMode,
  style,
  selectedItem,
  isProduction,
}) => {
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [expanded, setExpanded] = useState(false); // State for tracking expanded accordion
  const [controller, dispatch] = useMaterialUIController();

  useEffect(() => {
    const fetchRecentEmails = async () => {
      setLoading(true);
      try {
        const emailTo = selectedItem.email; // Adjust as needed
        const response = await RecentEmailModal(emailTo, isProduction);
        setEmails(response); // Assuming response contains the emails
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    setExpanded(false);
    if (open) {
      fetchRecentEmails();
    }
  }, [open, selectedItem]);

  function htmlToPlainText(html) {
    // Replace &nbsp; and &amp;nbsp; with regular spaces
    html = html
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;nbsp;/g, " ")
      .replace(/&#160;/g, " ");

    // Create a temporary element to hold the HTML
    const tempElement = document.createElement("div");
    tempElement.innerHTML = html;

    // Get the text content
    let plainText = tempElement.textContent || tempElement.innerText || "";

    // Normalize spaces and trim leading/trailing spaces
    plainText = plainText.replace(/\s+/g, " ").trim();

    return plainText; // No need for extra space removal; trim() handles it
  }

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false); // Control expanded state
  };

  const handleDownload = (fileName) => {
    RecentEmailAttachementDownload(
      (res) => {
        setLoading(false);

        // Create a Blob URL from the response data
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName); // Set the file name for download
        document.body.appendChild(link);
        link.click(); // Trigger the download
        link.remove(); // Clean up the link element
        ShowSuccessMessage(dispatch, "File downloaded successfully");
      },
      (err) => {
        setLoading(false);
        console.log(err);
        ShowErrorMessage(dispatch, "Error downloading file");
      },
      fileName
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="recent-emails"
      aria-describedby="recent-emails-description"
      className="responsive-modal recent-emails"
    >
      <div className={darkMode ? "darkmode_wrap" : "lightmode_wrap"}>
        <Paper elevation={3} sx={style.modal}>
          <Box
            onClick={handleClose}
            className="close-modal-sm"
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              cursor: "pointer",
            }}
          >
            <CloseRounded color={darkMode ? "white" : "secondary"} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              color: darkMode ? "#FFFFFF" : "#000000",
              mb: 1,
            }}
          >
            Recent Emails
          </Typography>
          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100px"
            >
              <CircularProgress
                size={25}
                sx={{ color: darkMode ? "#FFFFFF" : "#000000" }}
              />
            </Box>
          ) : (
            <List>
              {emails?.length > 0 ? (
                emails.map((item, index) => (
                  <Accordion
                    key={index}
                    expanded={expanded === `panel${index}`} // Use expanded state
                    onChange={handleChange(`panel${index}`)} // Handle accordion changes
                    sx={{
                      border: "1px solid #fff",
                      bgcolor: darkMode ? "#1b2034" : "#fff",
                      mb: 2,
                      borderRadius: "4px",
                    }}
                    className="arrow-order"
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{ color: darkMode ? "#FFFFFF" : "#000000" }}
                        />
                      }
                      aria-controls={`panel${index}a-content`}
                      id={`panel${index}a-header`}
                    >
                      <ListItemText
                        primary={item.subject}
                        primaryTypographyProps={{
                          sx: { color: darkMode ? "#FFFFFF" : "#000000" },
                        }}
                        className="recent-email-title"
                      />
                      <ListItemText
                        primary={
                          expanded === `panel${index}`
                            ? moment(item.sentOn).format(
                                "ddd DD/MM/YYYY hh:mm A"
                              )
                            : moment(item.sentOn).format("ddd hh:mm A")
                        }
                        primaryTypographyProps={{
                          sx: { color: darkMode ? "#FFFFFF" : "#000000" },
                        }}
                        className="recent-email-date"
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      {item?.fileName !== null && (
                        <Button
                          variant="outlined"
                          endIcon={<FileDownloadOutlinedIcon />}
                          sx={{
                            color: darkMode ? "#fff" : "#000",
                            borderColor: darkMode ? "#fff" : "#000",
                            marginBottom: "20px",
                            borderRadius: "4px",
                            fontWeight: "400",
                            backgroundColor: darkMode
                              ? "#20273a"
                              : "transparent",
                            textTransform: "capitalize",
                            "&:hover": {
                              backgroundColor: darkMode
                                ? "#20273a"
                                : "transparent",
                              borderColor: darkMode ? "#fff" : "#000",
                              opacity: "1",
                            },
                          }}
                          onClick={() => handleDownload(item.fileName)}
                        >
                          {item?.fileName}
                        </Button>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: darkMode ? "#FFFFFF" : "#000000",
                        }}
                        className="recent-email-msg"
                      >
                        {htmlToPlainText(item.message)}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: darkMode ? "#FFFFFF" : "#000000",
                        }}
                        className="recent-email-sender"
                      >
                        <b>Sent by:</b> {item.fullName}
                        <br />
                        <b>Email:</b> {item.sentBy}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: darkMode ? "#FFFFFF" : "#000000",
                    textAlign: "center",
                    mt: 2,
                  }}
                >
                  No recent emails found.
                </Typography>
              )}
            </List>
          )}
        </Paper>
      </div>
    </Modal>
  );
};

export default RecentEmails;
